import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GOLD_V2_TICKS } from '@wikigold/engine';
import { GameCanvas } from '../components/GameCanvas';
import { api, errorMessage } from '../lib';
import { gainFromScore } from './catch-feedback';
import { GoldTimeline, type GoldRunView } from './gold-timeline';
import { createSettlementRecovery, type SettlementStatus } from './settlement-recovery';

export type { GoldRunView } from './gold-timeline';

export function GoldGame({ initial, onSettled, onError }: {
  initial: GoldRunView;
  onSettled: (run: GoldRunView) => void | Promise<void>;
  onError: (message: string) => void;
}): JSX.Element {
  const { t } = useTranslation();
  const [timeline] = useState(() => new GoldTimeline(initial, performance.now()));
  const [display, setDisplay] = useState(initial.state);
  const [connected, setConnected] = useState(false);
  const [pending, setPending] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [catchNotice, setCatchNotice] = useState<{ id: number; amount: number } | null>(null);
  const [settlementStatus, setSettlementStatus] = useState<SettlementStatus>('idle');
  const socket = useRef<WebSocket | null>(null);
  const settlement = useRef<ReturnType<typeof createSettlementRecovery> | null>(null);
  const resync = useRef({ inFlight: false, retryAt: 0 });
  const mounted = useRef(false);
  const lastGoldAmount = useRef(initial.state.goldAmount);
  const catchNoticeId = useRef(0);
  const hudGate = useRef({ at: 0, tick: -1, phase: '', hook: '', shots: -1 });
  const callbacks = useRef({ onSettled, onError });
  callbacks.current = { onSettled, onError };
  useEffect(() => {
    mounted.current = true;
    const recovery = createSettlementRecovery(run => callbacks.current.onSettled(run), setSettlementStatus);
    settlement.current = recovery;
    recovery.accept(timeline.confirmed);
    return () => {
      mounted.current = false;
      recovery.dispose();
    };
  }, [timeline]);
  const consume = (run: GoldRunView, acknowledged = false, reconnected = false) => {
    if (!mounted.current) return;
    const accepted = timeline.receive(run, performance.now(), reconnected);
    if (acknowledged || timeline.pending.length === 0 || run.phase !== 'RUNNING') setPending(false);
    if (!accepted) return;
    settlement.current?.accept(run);
  };
  const syncCheckpoint = () => {
    const now = performance.now();
    if (!mounted.current || resync.current.inFlight || now < resync.current.retryAt) return;
    resync.current.inFlight = true;
    resync.current.retryAt = now + 1200;
    void api<GoldRunView>(`/api/gold-v2/runs/${initial.id}`).then(consume).catch(() => {
      if (mounted.current) setFeedback(t('game.syncing'));
    }).finally(() => { resync.current.inFlight = false; });
  };
  useEffect(() => {
    let disposed = false;
    let retry: ReturnType<typeof setTimeout> | undefined;
    const connect = () => {
      if (disposed) return;
      const ws = new WebSocket(`${location.protocol === 'https:' ? 'wss:' : 'ws:'}//${location.host}/api/gold-v2/runs/${initial.id}/ws`);
      socket.current = ws;
      ws.onmessage = event => {
        if (disposed || socket.current !== ws) return;
        let data: { type?: string; run?: GoldRunView; error?: string };
        try { data = JSON.parse(String(event.data)); }
        catch { setFeedback(t('game.networkIncomplete')); return; }
        if (data.type === 'taken_over') {
          callbacks.current.onError(t('game.takeover'));
          return;
        }
        if (data.run) {
          setConnected(true);
          consume(data.run as GoldRunView, data.type === 'ack', data.type === 'ready');
        }
        if (data.error) {
          setPending(false);
          timeline.discardPending(performance.now());
          if (data.error === 'hook_busy') {
            setFeedback(t('game.waitReturn'));
          } else if (['terminal', 'received_tick_elapsed', 'sequence'].includes(data.error)) {
            setFeedback(t('game.syncing'));
          } else {
            callbacks.current.onError(errorMessage(new Error(String(data.error))));
          }
          void api<GoldRunView>(`/api/gold-v2/runs/${initial.id}`).then(consume).catch(() => {});
        }
      };
      ws.onclose = () => {
        if (disposed || socket.current !== ws) return;
        setConnected(false);
        setPending(false);
        // The new socket's ready checkpoint confirms accepted input and
        // drops unconfirmed offline clicks. Never resend old input.
        timeline.needsResync = true;
        retry = setTimeout(connect, 1200);
      };
      ws.onerror = () => ws.close();
    };
    const wake = () => {
      if (!disposed && document.visibilityState === 'visible') syncCheckpoint();
    };
    connect();
    document.addEventListener('visibilitychange', wake);
    return () => {
      disposed = true;
      clearTimeout(retry);
      document.removeEventListener('visibilitychange', wake);
      socket.current?.close();
    };
  }, [initial.id, timeline]);
  const needsTerminalRecovery = display.tick >= GOLD_V2_TICKS - 1 && settlementStatus === 'idle';
  useEffect(() => {
    if (!needsTerminalRecovery) return;
    let active = true;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const recover = async () => {
      try {
        const run = await api<GoldRunView>(`/api/gold-v2/runs/${initial.id}`);
        if (!active) return;
        consume(run);
        if (run.phase === 'RUNNING') timer = setTimeout(recover, 800);
      } catch {
        if (active) setFeedback(t('game.settling'));
        if (active) timer = setTimeout(recover, 1200);
      }
    };
    void recover();
    return () => {
      active = false;
      if (timer) clearTimeout(timer);
    };
  }, [initial.id, needsTerminalRecovery, timeline]);
  useEffect(() => {
    if (!feedback) return;
    const timer = setTimeout(() => setFeedback(''), 1800);
    return () => clearTimeout(timer);
  }, [feedback]);
  useEffect(() => {
    if (!catchNotice) return;
    const timer = setTimeout(() => setCatchNotice(null), 1250);
    return () => clearTimeout(timer);
  }, [catchNotice]);
  const release = () => {
    if (!connected || pending || socket.current?.readyState !== WebSocket.OPEN) return;
    const input = timeline.input();
    if (!input) {
      setFeedback(t('game.waitReturn'));
      return;
    }
    setPending(true);
    socket.current.send(JSON.stringify(input));
  };
  const remaining = Math.max(0, Math.ceil((GOLD_V2_TICKS - display.tick) / 30));
  return <section className="gold-game" data-phase={display.phase}>
    <div className="gold-game-hud gold-game-hud-simple">
      <div className="gold-game-time"><span>{t('game.remaining')}</span><strong>{remaining}<small>{t('common.seconds')}</small></strong></div>
      <div className="gold-score"><span>{t('game.gold')}</span><b>{display.goldAmount}</b><small>g</small></div>
    </div>
    <div className="gold-status" role="status">
      {settlementStatus !== 'idle' ? t('play.saved') : !connected ? t('game.connecting') : feedback || (display.hook.status === 'RETRACTING' ? t('game.waitReturn') : t('game.status', { seconds: remaining, score: display.goldAmount }))}
    </div>
    {settlementStatus === 'retry' && <button className="gold-secondary" data-testid="gold-settlement-retry" onClick={() => { void settlement.current?.retry(); }}>{t('common.retry')}</button>}
    <GameCanvas
      state={display}
      sample={timeline.sample}
      onFrame={frame => {
        if (timeline.needsResync) syncCheckpoint();
        const gain = gainFromScore(lastGoldAmount.current, frame.goldAmount);
        lastGoldAmount.current = frame.goldAmount;
        if (gain > 0) setCatchNotice({ id: ++catchNoticeId.current, amount: gain });
        const now = performance.now();
        const gate = hudGate.current;
        const structural = frame.phase !== gate.phase || frame.hook.status !== gate.hook || frame.shots !== gate.shots;
        if (structural || now - gate.at >= 100) {
          hudGate.current = { at: now, tick: frame.tick, phase: frame.phase, hook: frame.hook.status, shots: frame.shots };
          setDisplay(frame);
        }
      }}
    />
    {catchNotice && <div key={catchNotice.id} className="gold-catch-pop" data-testid="gold-catch-pop" role="status" aria-live="polite">
      <span>✦</span><small>{t('game.catchSuccess')}</small><strong>+{catchNotice.amount}g</strong>
    </div>}
    <button className="gold-release" data-testid="gold-release" disabled={!connected || pending || display.phase !== 'RUNNING' || display.hook.status !== 'SWINGING'} onClick={release}>
      {display.phase === 'SETTLED' ? t('game.ended') : display.hook.status === 'SWINGING' ? t('game.release') : t('game.retracting')}
    </button>
    <p className="gold-fineprint">{t('game.fineprint')}</p>
  </section>;
}
